import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import i18n from '../i18n';
import api, { cleanupUserData, setDefaultAuthorizationHeader } from '../services';
import { auth0Audience } from '../config';

const useAxiosInterceptor = () => {
    const { getAccessTokenSilently, logout } = useAuth0();

    useEffect(() => {
        const interceptor = api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const statusCode = error.response?.status;
                const responseData = error?.response?.data;

                if (statusCode === 401) {
                    try {
                        const newToken = await getAccessTokenSilently({
                            authorizationParams: { audience: auth0Audience },
                        });
                        setDefaultAuthorizationHeader(newToken);
                        console.log('Token refreshed successfully');
                        error.config.headers['Authorization'] = `Bearer ${newToken}`;

                        return api.request(error.config);
                    } catch (err) {
                        console.error('Token refresh failed');
                        cleanupUserData();
                        await logout({ logoutParams: { returnTo: window.location.origin } });

                        return;
                    }
                }

                if (statusCode === 403) {
                    console.error('403 Forbidden - Insufficient Access Rights');
                    throw new Error('Insufficient Access Rights'); // TODO: maybe redirect to an error page or show a message
                }

                if (responseData?.code === 'tokenExpired') {
                    console.error('Token expired by code');
                    cleanupUserData();
                    await logout({ logoutParams: { returnTo: window.location.origin } });
                    return;
                }

                if (responseData?.message) {
                    if (responseData.errors && typeof responseData.errors === 'object') {
                        const fieldErrors = Object.entries(responseData.errors)
                            .map(([field, msg]) => `${field}: ${msg}`)
                            .join(', ');
                        error.message = fieldErrors || responseData.message;
                    } else if (responseData.key) {
                        error.message = i18n.t(responseData.key, {
                            ns: 'errors',
                            defaultValue: responseData.message,
                            ...(responseData.params || {}),
                        });
                    } else if (responseData.params) {
                        error.message = i18n.t(responseData.message, responseData.params);
                    } else {
                        error.message = responseData.message;
                    }
                }

                throw error;
            }
        );

        return () => api.interceptors.response.eject(interceptor);
    }, [getAccessTokenSilently, logout]);
};

export default useAxiosInterceptor;
